import React, { useEffect, useRef, useState } from "react";
import { gaTrackEvent } from "../../analytics/ga4";
import { pageGutterPx, responsiveGridColumns } from "../../core/breakpoints";
import { C, VI_PRICE_MAP } from "../../core/constants";
import { useI18n } from "../i18n/LocaleProvider";
import { APP_FONT_SIZE } from "../../core/theme";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { CREDIT_COST_REFERENCE, CREDIT_PACK_BASE, SUBSCRIPTION_PRODUCT_CODE } from "../billing/config";
import { createMomoPayment } from "./api";
import { createPaypalPayment, capturePaypalPayment } from "./paypal/api";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";

function ga4PurchaseDedupeKey(transactionId) {
  return transactionId ? `ecliptica.ga4Purchase:${transactionId}` : null;
}

export default function PaymentPage({
  setPage,
  acc,
  selectedProduct,
  billingCatalog = [],
  t,
}) {
  const { locale: uiLocale } = useI18n();
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  // Temporary rollout control: keep MoMo implementation in code, but hide it in UI.
  const showMomoOption = false;
  const [method, setMethod] = useState("processor"); // processor | momo
  const [momoPhone, setMomoPhone] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const paypalContainerRef = useRef(null);
  const [paypalError, setPaypalError] = useState("");
  const [paypalLoading, setPaypalLoading] = useState(false);
  const purchaseLoggedRef = useRef(false);
  const paypalProviderIdRef = useRef(null);

  const fireBeginCheckout = () => {
    try {
      const k = `ecliptica.ga4BeginCheckout:${pack.code}:${pack.productType}`;
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, "1");
    } catch (_) {
      // ignore
    }
    const usdRow =
      pickPrice(fallbackProduct.prices || [], "USD", desiredInterval) ||
      pickPrice(fallbackProduct.prices || [], "USD", "one_time");
    const value = Number.isFinite(usdRow?.amount_minor) ? usdRow.amount_minor / 100 : undefined;
    gaTrackEvent("begin_checkout", {
      value,
      currency: "USD",
      items: [
        {
          item_id: String(pack.code),
          item_name: pack.name,
          item_category: pack.productType,
          quantity: 1,
          price: value,
        },
      ],
    });
  };

  useEffect(() => {
    if (!showMomoOption && method === "momo") setMethod("processor");
  }, [showMomoOption, method]);

  const defaultPack = CREDIT_PACK_BASE.find((item) => item.key === "nebula");

  // AppShell should rehydrate `selectedProduct` from the URL query params.
  // During cold-loads, there can be a short moment where `selectedProduct`
  // is still null; fall back to `product_code` to avoid showing nebula.
  let productCodeFromUrl = null;
  let productTypeFromUrl = null;
  try {
    const params = new URLSearchParams(window.location.search || "");
    productCodeFromUrl = params.get("product_code");
    productTypeFromUrl = params.get("product_type");
  } catch (_) {
    // ignore
  }

  const parseUsdMinor = (usdString) => {
    // "$2.99" -> 299
    const raw = String(usdString || "").replace(/[^0-9.]/g, "");
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 100);
  };

  const parseVndMinor = (vndString) => {
    // "59,000₫" -> 59000
    const digits = String(vndString || "").replace(/[^\d]/g, "");
    const value = parseInt(digits, 10);
    return Number.isFinite(value) ? value : null;
  };

  // If billing catalog fails to load, we still want the UI to reflect the URL selection.
  const urlFallbackProduct = (() => {
    if (!productCodeFromUrl) return null;

    // Subscription fallback (per month)
    if (productTypeFromUrl === "subscription" || productCodeFromUrl === SUBSCRIPTION_PRODUCT_CODE) {
      return {
        code: SUBSCRIPTION_PRODUCT_CODE,
        product_type: "subscription",
        credits_amount: 0,
        prices: [
          { amount_minor: 199_000, currency: "VND", billing_interval: "month" },
          { amount_minor: 999, currency: "USD", billing_interval: "month" },
        ],
      };
    }

    // Credit pack fallback
    const packFromBase = CREDIT_PACK_BASE.find((p) => p.key === productCodeFromUrl) || null;
    if (!packFromBase) return null;

    const usdPriceMinor = parseUsdMinor(packFromBase.usdPrice);
    const vndPriceStr = VI_PRICE_MAP?.[packFromBase.usdPrice];
    const vndPriceMinor = parseVndMinor(vndPriceStr);
    if (!usdPriceMinor || !vndPriceMinor) return null;

    return {
      code: packFromBase.key,
      product_type: "credit_pack",
      credits_amount: packFromBase.credits,
      prices: [
        { amount_minor: vndPriceMinor, currency: "VND", billing_interval: "one_time" },
        { amount_minor: usdPriceMinor, currency: "USD", billing_interval: "one_time" },
      ],
    };
  })();

  const effectiveSelectedProduct =
    (productCodeFromUrl ? billingCatalog.find((item) => item.code === productCodeFromUrl) || null : null) ||
    (selectedProduct && Array.isArray(selectedProduct.prices) && selectedProduct.prices.length > 0 ? selectedProduct : null) ||
    urlFallbackProduct;

  const fallbackProduct =
    effectiveSelectedProduct ||
    billingCatalog.find((item) => item.code === "nebula") ||
    {
      code: "nebula",
      product_type: "credit_pack",
      credits_amount: defaultPack?.credits || 25,
      prices: [
        { amount_minor: 119000, currency: "VND", billing_interval: "one_time" },
        { amount_minor: 599, currency: "USD", billing_interval: "one_time" },
      ],
    };

  // MoMo is always VND. PayPal/processor is always USD (regardless of UI language).
  const desiredCurrencyForMethod = method === "momo" ? "VND" : "USD";
  const desiredInterval = fallbackProduct.product_type === "subscription" ? "month" : "one_time";
  const pickPrice = (prices = [], currency, interval) => (
    prices.find((row) => row.currency === currency && row.billing_interval === interval) ||
    prices.find((row) => row.currency === "USD" && row.billing_interval === interval) ||
    prices.find((row) => row.currency === "VND" && row.billing_interval === interval) ||
    prices.find((row) => row.billing_interval === interval) ||
    prices[0]
  );
  const priceRecord = pickPrice(fallbackProduct.prices || [], desiredCurrencyForMethod, desiredInterval);

  const formatMoney = (amountMinor, currency) => {
    if (!Number.isFinite(amountMinor)) return "--";
    const normalizedCurrency = (currency || "VND").toUpperCase();
    const fractionDigits = normalizedCurrency === "USD" ? 2 : 0;
    const displayAmount = normalizedCurrency === "USD" ? amountMinor / 100 : amountMinor;
    try {
      return new Intl.NumberFormat(uiLocale === "vi" ? "vi-VN" : "en-US", {
        style: "currency",
        currency: normalizedCurrency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(displayAmount);
    } catch (_) {
      return `${displayAmount}`;
    }
  };

  const pack = {
    code: fallbackProduct.code,
    productType: fallbackProduct.product_type,
    name: fallbackProduct.product_type === "subscription" ? t("eclipse_pass") : t(`pack_${fallbackProduct.code}`),
    credits: fallbackProduct.credits_amount || 0,
    price: formatMoney(priceRecord?.amount_minor, priceRecord?.currency || desiredCurrencyForMethod),
    currency: priceRecord?.currency || desiredCurrencyForMethod,
    color: fallbackProduct.code === "stardust" ? acc.tertiary : fallbackProduct.code === "supernova" ? acc.secondary : acc.primary,
  };

  useEffect(() => {
    if (!done || purchaseLoggedRef.current) return;
    const transactionId = paypalProviderIdRef.current ? String(paypalProviderIdRef.current) : undefined;
    const dedupeKey = ga4PurchaseDedupeKey(transactionId);
    if (dedupeKey) {
      try {
        if (sessionStorage.getItem(dedupeKey)) {
          purchaseLoggedRef.current = true;
          return;
        }
      } catch (_) {
        // ignore
      }
    }
    purchaseLoggedRef.current = true;
    const usdRow =
      pickPrice(fallbackProduct.prices || [], "USD", desiredInterval) ||
      pickPrice(fallbackProduct.prices || [], "USD", "one_time");
    const value = Number.isFinite(usdRow?.amount_minor) ? usdRow.amount_minor / 100 : undefined;
    gaTrackEvent("purchase", {
      ...(transactionId ? { transaction_id: transactionId } : {}),
      value,
      currency: "USD",
      items: [
        {
          item_id: String(pack.code),
          item_name: pack.name,
          item_category: pack.productType,
          quantity: 1,
          price: value,
        },
      ],
    });
    if (dedupeKey) {
      try {
        sessionStorage.setItem(dedupeKey, "1");
      } catch (_) {
        // ignore
      }
    }
    // Intentionally only `done`: log once when payment completes; pack/prices match this render.
  }, [done]);

  useEffect(() => {
    if (method !== "processor" || done) return;
    if (!paypalContainerRef.current) return;

    const clientId = window?.PAYPAL_CLIENT_ID;
    if (!clientId) {
      setPaypalError(t("paypal_error_missing_client_id"));
      return;
    }

    setPaypalError("");

    const intent = pack.productType === "subscription" ? "subscription" : "capture";
    const isSubscriptionPack = pack.productType === "subscription";
    /** PayPal recommends loading currency with the SDK; guest card flows often fail without it. Vault on one-time orders breaks many sandbox card paths. */
    const sandboxSdk = typeof window !== "undefined" && window.PAYPAL_SANDBOX === true;
    /** SPA: subscription vs capture need different SDK query params — fingerprint so we reload the right script. */
    const sdkFingerprint = `${intent}|${isSubscriptionPack ? "vault" : "novault"}`;
    const scriptSelector = `script[data-ecliptica-paypal-sdk="true"][data-ecliptica-paypal-fp="${sdkFingerprint}"]`;

    let effectActive = true;

    function tearDownMismatchedPayPalSdk() {
      document.querySelectorAll("script[data-ecliptica-paypal-sdk]").forEach((el) => {
        if (el.getAttribute("data-ecliptica-paypal-fp") !== sdkFingerprint) el.remove();
      });
      try {
        delete window.paypal;
      } catch (_) {
        window.paypal = undefined;
      }
    }

    const renderButtons = () => {
      if (!effectActive || !paypalContainerRef.current) return;
      try {
        if (!window?.paypal?.Buttons) throw new Error(t("paypal_error_sdk_not_ready"));
        paypalContainerRef.current.innerHTML = "";

        const isSubscription = isSubscriptionPack;
        const paypalButtonConfig = isSubscription
          ? {
              createSubscription: async () => {
                setPaypalLoading(true);
                setPaypalError("");
                try {
                  const resp = await createPaypalPayment({
                    productCode: pack.code,
                    productType: pack.productType,
                  });
                  const providerId = resp?.payment?.provider_id?.toString();
                  if (!providerId) throw new Error(t("paypal_error_init_subscription"));
                  return providerId;
                } catch (e) {
                  setPaypalError(e?.message || t("paypal_error_init_subscription"));
                  throw e;
                } finally {
                  setPaypalLoading(false);
                }
              },
            }
          : {
              createOrder: async () => {
                setPaypalLoading(true);
                setPaypalError("");
                try {
                  const resp = await createPaypalPayment({
                    productCode: pack.code,
                    productType: pack.productType,
                  });
                  const providerId = resp?.payment?.provider_id?.toString();
                  if (!providerId) throw new Error(t("paypal_error_init_payment"));
                  return providerId;
                } catch (e) {
                  setPaypalError(e?.message || t("paypal_error_init_payment"));
                  throw e;
                } finally {
                  setPaypalLoading(false);
                }
              },
            };

        // One-composite Smart Buttons (`layout: vertical`). Matches historical behavior: debit/credit uses
        // PayPal’s hosted popup, not standalone CARD fields (those require funding-eligibility + split renders).
        // One-time capture: encourage hosted popup-style card UX (not subscription — onShippingChange is unsupported there).
        const orderHostedCheckoutHelpers = !isSubscription
          ? {
              onShippingChange(_data, actions) {
                return actions.resolve();
              },
            }
          : {};

        const renderResult = window.paypal
          .Buttons({
            style: {
              shape: "pill",
              color: "gold",
              layout: "vertical",
              label: isSubscription ? "subscribe" : "pay",
              tagline: false,
            },
            ...orderHostedCheckoutHelpers,
            onApprove: async (data) => {
              try {
                const providerId =
                  data?.subscriptionID ||
                  data?.orderID ||
                  data?.id ||
                  data?.provider_id;
                if (!providerId) throw new Error(t("paypal_error_missing_provider_id"));

                await capturePaypalPayment({ providerId: providerId.toString() });
                paypalProviderIdRef.current = providerId.toString();
                setDone(true);
              } catch (e) {
                setPaypalError(e?.message || t("paypal_error_payment_failed"));
              }
            },
            onError: (err) => {
              setPaypalError(err?.message || t("paypal_error_generic"));
            },
            ...paypalButtonConfig,
          })
          .render(paypalContainerRef.current);
        const afterRender = () => {
          try {
            fireBeginCheckout();
          } catch (_) {
            // ignore
          }
        };
        if (renderResult && typeof renderResult.then === "function") {
          renderResult.then(afterRender).catch(() => {});
        } else {
          afterRender();
        }
      } catch (e) {
        if (!effectActive) return;
        setPaypalError(e?.message || t("paypal_error_render_buttons"));
      }
    };

    tearDownMismatchedPayPalSdk();

    const existingScript = document.querySelector(scriptSelector);

    if (window?.paypal?.Buttons && existingScript) {
      renderButtons();
      return () => {
        effectActive = false;
      };
    }

    if (existingScript && !window?.paypal?.Buttons) {
      existingScript.addEventListener("load", renderButtons, { once: true });
      return () => {
        effectActive = false;
      };
    }

    const sdkQuery = new URLSearchParams({
      "client-id": clientId,
      intent,
      currency: "USD",
      vault: isSubscriptionPack ? "true" : "false",
    });
    if (sandboxSdk) sdkQuery.set("buyer-country", "US");

    const script = document.createElement("script");
    script.setAttribute("data-ecliptica-paypal-sdk", "true");
    script.setAttribute("data-ecliptica-paypal-fp", sdkFingerprint);
    script.src = `https://www.paypal.com/sdk/js?${sdkQuery.toString()}`;
    script.async = true;
    script.onload = renderButtons;
    script.onerror = () => {
      if (!effectActive) return;
      setPaypalError(t("paypal_error_load_sdk"));
    };
    document.body.appendChild(script);

    return () => {
      effectActive = false;
    };
  }, [method, done, pack.code, pack.productType]);

  const inputStyle = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10,
    color: C.textPrimary,
    padding: "12px 14px",
    fontSize: fs - 2,
    fontFamily: "'DM Sans',sans-serif",
    outline: "none",
  };

  if (done) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 1 }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 64, marginBottom: 20, color: pack.color }}>✦</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 48, fontWeight: 400, color: C.textPrimary, marginBottom: 12 }}>
          {t("payment_complete_heading")}
        </h2>
        <p style={{ color: C.textSecondary, fontSize: fs, marginBottom: 32 }}>
          {pack.productType === "subscription"
            ? <><strong style={{ color: pack.color }}>{pack.name}</strong>{t("payment_complete_subscription_trailing")}</>
            : <><strong style={{ color: pack.color }}>{pack.credits} {pack.name} credits</strong>{t("payment_complete_credits_trailing")}</>}
        </p>
        <Button variant="primary" acc={acc} onClick={() => setPage("dashboard")} style={{ padding: "13px 40px", fontSize: fs }}>
          {t("payment_enter_cosmos_button")}
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      zIndex: 1,
      padding: gutter,
    }}>
      <div style={{ position: "fixed", inset: 0, background: `radial-gradient(ellipse 60% 70% at 50% 50%, ${acc.secondary}0a 0%, transparent 70%)`, pointerEvents: "none" }} />

      <div
        style={{
          width: "100%",
          maxWidth: 820,
          display: "grid",
          gridTemplateColumns: responsiveGridColumns(isMobile, { desktop: "1fr 340px", mobile: "1fr" }),
          gap: 28,
          alignItems: "start",
          position: "relative",
        }}
      >

        {/* LEFT — Payment form */}
        <GlassCard style={{ padding: 36 }} glow accent={acc.primary}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 28,
            }}
          >
            <h2
              style={{
                fontFamily: "'Cormorant Garamond',serif",
                fontSize: 30,
                fontWeight: 400,
                color: C.textPrimary,
                margin: 0,
                flex: 1,
                minWidth: 0,
                lineHeight: 1.25,
                paddingRight: 4,
              }}
            >
              {pack.productType === "subscription"
                ? t("start_eclipse_pass")
                    .replace("%{price}", pack.price)
                    // Keep `/mo`, `/tháng`, etc. on the same line as the slash (hosted UI widths).
                    .replace("/", "/\u2060")
                : t("payment_complete_payment_title")}
            </h2>
            <button
              type="button"
              onClick={() => setPage("creditsPlans")}
              style={{
                flexShrink: 0,
                margin: 0,
                padding: "4px 0 4px 12px",
                border: "none",
                background: "transparent",
                fontSize: 13,
                color: C.textMuted,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span aria-hidden>←</span>
              {t("payment_back")}
            </button>
          </div>

          {/* Method tabs */}
          <div style={{ display: "flex", gap: 12, marginBottom: 28 }}>
            {[
              {
                id: "processor",
                label: (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <img
                      src="/icons/paypal-dark.svg"
                      alt="PayPal"
                      style={{
                        width: 20,
                        height: 20,
                        filter: "drop-shadow(0 0 8px rgba(10, 179, 255, 0.45))",
                      }}
                    />
                    <span>PayPal</span>
                  </span>
                ),
                sub: t("payment_method_paypal_sub"),
              },
              ...(showMomoOption ? [{ id: "momo", label: "🟣  MoMo", sub: "Ví MoMo · Việt Nam" }] : []),
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                style={{
                  flex: 1,
                  padding: "14px 16px",
                  borderRadius: 12,
                  cursor: "pointer",
                  border: `1px solid ${method === m.id ? acc.primary + "66" : "rgba(255,255,255,0.08)"}`,
                  background: method === m.id ? `${acc.primary}18` : "rgba(255,255,255,0.03)",
                  textAlign: "left",
                  transition: "all 0.2s",
                  boxShadow: method === m.id ? `0 0 20px ${acc.primary}22` : "none",
                }}
              >
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 14, color: method === m.id ? C.textPrimary : C.textSecondary }}>
                  {m.label}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>{m.sub}</div>
              </button>
            ))}
          </div>

          {method === "processor" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.6, padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                {t("payment_redirect_paypal")}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", marginTop: 4 }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span style={{ fontSize: 12, color: C.textMuted }}>{t("payment_secure_paypal")}</span>
              </div>

              <div style={{ fontSize: 12, color: C.textMuted, padding: "0 6px" }}>
                {pack.productType === "subscription"
                  ? t("start_eclipse_pass").replace("%{price}", pack.price)
                  : t("payment_continue_paypal").replace("%{price}", pack.price)}
              </div>

              <div
                ref={paypalContainerRef}
                className="ecliptica-paypal-host"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  width: "100%",
                  gap: 0,
                  marginBottom: 0,
                  paddingTop: 6,
                  paddingBottom: 0,
                }}
              />

              {paypalLoading && (
                <div style={{ color: C.textMuted, fontSize: 12 }}>{t("credits_insights_loading")}</div>
              )}

              {paypalError && (
                <div style={{ color: "#ff9aa4", fontSize: 12 }}>{paypalError}</div>
              )}
            </div>
          )}

          {method === "momo" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* MoMo branding */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderRadius: 12, background: "rgba(162,32,134,0.12)", border: "1px solid rgba(162,32,134,0.35)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg,#a22086,#d83b9a)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🟣</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: C.textPrimary }}>Ví MoMo</div>
                  <div style={{ fontSize: 12, color: "#d83b9a" }}>Thanh toán nhanh · Hoàn tiền dễ dàng</div>
                </div>
              </div>

              {/* QR code placeholder */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 20px", borderRadius: 14, border: `1px dashed rgba(255,255,255,0.12)`, background: "rgba(255,255,255,0.02)" }}>
                {/* QR grid placeholder */}
                <div style={{ width: 160, height: 160, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 2, padding: 10, marginBottom: 14 }}>
                  {Array.from({ length: 64 }).map((_, i) => (
                    <div key={i} style={{ borderRadius: 1, background: Math.random() > 0.5 ? "rgba(162,32,134,0.8)" : "transparent" }} />
                  ))}
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, textAlign: "center", lineHeight: 1.6 }}>
                  Mở ứng dụng MoMo và quét mã QR<br />
                  <span style={{ color: "#d83b9a" }}>Hết hạn sau: 10:00</span>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
                <span style={{ fontSize: 12, color: C.textMuted }}>hoặc nhập số điện thoại</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
              </div>

              <div>
                <div style={{ fontSize: 11, color: "#d83b9a", letterSpacing: "0.1em", marginBottom: 7 }}>SỐ ĐIỆN THOẠI MOMO</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", fontSize: 14, color: C.textSecondary, whiteSpace: "nowrap" }}>🇻🇳 +84</div>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder={t("payment_momo_phone_placeholder")} value={momoPhone} onChange={(e) => setMomoPhone(e.target.value)} />
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(162,32,134,0.07)", border: "1px solid rgba(162,32,134,0.2)" }}>
                <span style={{ fontSize: 14 }}>ℹ️</span>
                <span style={{ fontSize: 12, color: C.textMuted }}>Bạn sẽ nhận được thông báo xác nhận trong ứng dụng MoMo.</span>
              </div>

              <button
                onClick={async () => {
                  setSubmitting(true);
                  setErrorMessage("");
                  try {
                    const payload = await createMomoPayment({ productCode: pack.code });
                    const payUrl = payload?.payment?.pay_url;
                    if (!payUrl) throw new Error(t("momo_error_missing_pay_url"));
                    window.location.assign(payUrl);
                  } catch (error) {
                    setErrorMessage(error?.message || t("momo_error_create_payment"));
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                style={{
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  cursor: "pointer",
                  background: "linear-gradient(135deg,#a22086,#d83b9a)",
                  color: "#fff",
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 600,
                  fontSize: fs,
                  boxShadow: "0 0 30px rgba(162,32,134,0.5)",
                  opacity: submitting ? 0.75 : 1,
                }}
              >
                {submitting ? "Đang tạo thanh toán..." : `Thanh toán qua MoMo · ${pack.price}`}
              </button>

              {errorMessage && (
                <div style={{ color: "#ff9aa4", fontSize: 12, marginTop: 6 }}>{errorMessage}</div>
              )}
            </div>
          )}
        </GlassCard>

        {/* RIGHT — Order summary */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <GlassCard style={{ padding: 28 }} glow accent={pack.color}>
            <div style={{ fontSize: 11, color: pack.color, letterSpacing: "0.15em", marginBottom: 16, textTransform: "uppercase" }}>
              {t("payment_order_summary")}
            </div>

            {/* Token display */}
            {pack.productType === "subscription" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: `${pack.color}18`, border: `1px solid ${pack.color}33`, boxShadow: `0 0 18px ${pack.color}22`, color: pack.color, fontSize: 20 }}>
                  ∞
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{t("subscription_go_unlimited")}</div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 16 }}>
                {Array.from({ length: Math.min(pack.credits, 25) }).map((_, i) => (
                  <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%,${pack.color}ff,${pack.color}66)`, boxShadow: `0 0 6px ${pack.color}88` }} />
                ))}
                {pack.credits > 25 && <span style={{ fontSize: 11, color: pack.color, alignSelf: "center" }}>+{pack.credits - 25}</span>}
              </div>
            )}

            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, color: C.textPrimary, marginBottom: 4 }}>
              {pack.productType === "subscription" ? "" : (
                <>
                  {pack.credits}{" "}
                  <span style={{ fontSize: 16, color: C.textMuted }}>{t("credits")}</span>
                </>
              )}
            </div>

            <div style={{ fontSize: 14, color: pack.color, fontWeight: 600, marginBottom: 20 }}>{pack.name}</div>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 16 }}>
              {[
                { label: t("pack_price"), val: pack.price },
                { label: method === "momo" ? t("vat_8") : t("vat_10"), val: t("included") },
                { label: t("processing_fee"), val: pack.currency === "USD" ? "$0.00" : "0₫" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textMuted, marginBottom: 8 }}>
                  <span>{r.label}</span><span style={{ color: i === 0 ? C.textPrimary : C.textMuted }}>{r.val}</span>
                </div>
              ))}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.07)", fontWeight: 600, fontSize: 15, color: C.textPrimary }}>
                <span>{t("total")}</span><span style={{ color: pack.color }}>{pack.price}</span>
              </div>
            </div>
          </GlassCard>

          {/* Credit usage reminder */}
          <GlassCard style={{ padding: 20 }}>
            <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.1em", marginBottom: 12 }}>{t("credit_costs")}</div>
            {CREDIT_COST_REFERENCE.map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: C.textSecondary }}><span style={{ color: acc.primary, marginRight: 6 }}>{r.icon}</span>{t(r.labelKey === "zodiac_prediction" ? "zodiac_reading" : r.labelKey)}</span>
                <span style={{ color: acc.primary }}>{r.shortCost}</span>
              </div>
            ))}
          </GlassCard>

          <div style={{ fontSize: 11, color: C.textMuted, textAlign: "center", lineHeight: 1.7, padding: "0 4px" }}>
            {t("credits_never_expire_notice")}
          </div>
        </div>
      </div>
    </div>
  );
}
